import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const AppDataContext = createContext(null);

// Statuses considered "closed" - filtered out of the active orders cache
const CLOSED_STATUSES = ['Order Complete', 'Cancelled'];

export function AppDataProvider({ children }) {
  // Active orders only (Order Complete + Cancelled filtered out in JS)
  const [orders, setOrders] = useState([]);
  // Full order list including completed/cancelled - available immediately
  // since we fetch everything at once and just split it
  const [allOrders, setAllOrders] = useState([]);
  const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);

  const [clients, setClients] = useState([]);
  const [facebookEvents, setFacebookEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch everything upfront - filter in JS to avoid index requirements
      // and null-field issues with Firestore's not-in operator
      const [ordersSnap, clientsSnap, fbSnap] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'facebookEvents')),
      ]);

      const all = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Split into active vs full - costs nothing since data is already fetched
      const active = all.filter(o => !CLOSED_STATUSES.includes(o.status));

      setAllOrders(all);
      setAllOrdersLoaded(true);
      setOrders(active);
      setClients(clientsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setFacebookEvents(fbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    } catch (err) {
      console.error('AppDataContext: Error loading data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // No-op if already loaded - kept for API compatibility with OrdersPage
  const loadAllOrders = useCallback(() => {
    // allOrders is already populated at load time - nothing to do
  }, []);

  // Load once on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Selectively refresh one or more collections
  const refresh = useCallback(async (collections = ['orders', 'clients', 'facebookEvents']) => {
    const refreshMap = {
      orders: async () => {
        const snap = await getDocs(collection(db, 'orders'));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const active = all.filter(o => !CLOSED_STATUSES.includes(o.status));
        setAllOrders(all);
        setOrders(active);
      },
      clients: async () => {
        const snap = await getDocs(collection(db, 'clients'));
        setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      facebookEvents: async () => {
        const snap = await getDocs(collection(db, 'facebookEvents'));
        setFacebookEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
    };

    const tasks = collections.map(name => refreshMap[name]?.());
    Promise.all(tasks).catch(err => console.error('AppDataContext: Refresh error:', err));
  }, []);

  return (
    <AppDataContext.Provider value={{
      orders,           // Active orders only (excludes Order Complete + Cancelled)
      allOrders,        // All orders including completed/cancelled
      allOrdersLoaded,  // Always true after initial load
      loadAllOrders,    // No-op - kept for API compatibility
      clients,
      facebookEvents,
      loaded,
      loading,
      error,
      refresh
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error('useAppData must be used inside AppDataProvider');
  return ctx;
}